import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

export interface ApexChartProps {
  /** A `null` y-value is a hole in the data: ApexCharts breaks the line there
   *  instead of drawing through it, which is the only honest way to render a
   *  bucket that measured nothing. */
  series: { name: string; data: [number, number | null][] }[];
  type?: "line" | "area";
  colors?: string[];
  height?: number;
  /** Fixed y-axis lower bound. Omit to let ApexCharts auto-fit from the data. */
  yMin?: number;
  /** Fixed y-axis upper bound. Omit to let ApexCharts auto-fit from the data. */
  yMax?: number;
}

export const CHART_COLORS = ["#2e9e3a", "#8b5cf6", "#f79009", "#0ea5e9", "#e11d48", "#22a699", "#f97316", "#6366f1", "#c026d3", "#3b82f6"]
export const HEATMAP_COLORS = [
  "#2e9e3a", // very low
  "#22a699", // low
  "#f79009", // medium
  "#f97316", // high
  "#e11d48"  // very high
];

export function rotateColors(n: number, colors: string[] = CHART_COLORS): string[] {
  const shift = n % colors.length
  return [...colors.slice(shift), ...colors.slice(0, shift)]
}
function isDarkTheme(): boolean {
  const theme = document.documentElement.getAttribute("data-theme") ?? "";
  return ["dark", "night", "dracula", "halloween", "forest", "black", "luxury", "coffee"].includes(theme);
}

export const ApexChart = React.memo(function ApexChart({
  series,
  type = "line",
  colors = CHART_COLORS,
  height = 256,
  yMin,
  yMax,
}: ApexChartProps) {
  const [dark, setDark] = useState(isDarkTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setDark(isDarkTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  // options only changes when type/dark/colors/seriesCount change — never on new data
  const options: ApexOptions = useMemo(() => ({
    chart: {
      type,
      toolbar: { show: false },
      animations: { enabled: true, speed: 400, easing: "easeinout" },
      background: "transparent",
      redrawOnWindowResize: false,
    },
    theme: { mode: dark ? "dark" : "light" },
    colors,
    stroke: { curve: "smooth", width: 1.2 },
    markers: { size: 0, strokeWidth: 1, fillOpacity: 0.5, hover: { size: 5 } },
    ...(type === "area" && {
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.05,
          stops: [0, 85],
        },
      },
    }),
    dataLabels: { enabled: false },
    xaxis: {
      type: "datetime",
      labels: { datetimeUTC: false, style: { fontSize: "0.6875rem" } },
      tickAmount: 6,
    },
    yaxis: {
      ...(yMin !== undefined && { min: yMin }),
      ...(yMax !== undefined && { max: yMax }),
      axisBorder: { show: true, color: dark ? "#374151" : "#e5e7eb" },
      labels: {
        style: { fontSize: "0.6875rem" },
        formatter: (v: number) => {
          const abs = Math.abs(v);
          if (abs >= 1000) return Math.round(v).toString();
          if (abs >= 10) return v.toFixed(1);
          if (abs >= 1) return v.toFixed(2);
          return v.toFixed(3);
        },
      },
    },
    grid: { borderColor: dark ? "#374151" : "#e5e7eb", strokeDashArray: 3 },
    tooltip: {
      x: {
        show: true,
        // Show full date + time (incl. seconds) in local time, matching the axis (datetimeUTC: false)
        formatter: (val: number) =>
          new Date(val).toLocaleString([], { dateStyle: "medium", timeStyle: "medium" }),
      },
      shared: true,
      fillSeriesColor: true,
    },
    legend: { show: series.length > 1, fontSize: "0.75rem" },
  }), [type, dark, colors, series.length, yMin, yMax]);

  return (
    <ReactApexChart
      type={type}
      series={series}
      options={options}
      height={height}
    />
  );
});

// Backwards-compatible re-export
export function ApexLineChart(props: Omit<ApexChartProps, "type">) {
  return <ApexChart type="line" {...props} />;
}

// Heatmap chart (different series format: {x, y} instead of [timestamp, value])
export interface ApexHeatmapChartProps {
  series: { name: string; data: { x: string; y: number }[] }[];
  height?: number;
  showDataLabels?: boolean;
  maxValue?: number;
}

export const ApexHeatmapChart = React.memo(function ApexHeatmapChart({
  series,
  height = 300,
  showDataLabels = false,
  maxValue = 100,
}: ApexHeatmapChartProps) {
  const [dark, setDark] = useState(isDarkTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setDark(isDarkTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  const step = maxValue / 5 || 1;
  const ranges = [
    { from: 0, to: 0, color: dark ? "#1f2937" : "#f3f4f6", name: "0 kW" },
    { from: 0.01, to: step, color: HEATMAP_COLORS[0], name: `< ${step.toFixed(1)} kW` },
    { from: step, to: step * 2, color: HEATMAP_COLORS[1], name: `< ${(step * 2).toFixed(1)} kW` },
    { from: step * 2, to: step * 3, color: HEATMAP_COLORS[2], name: `< ${(step * 3).toFixed(1)} kW` },
    { from: step * 3, to: step * 4, color: HEATMAP_COLORS[3], name: `< ${(step * 4).toFixed(1)} kW` },
    { from: step * 4, to: maxValue * 1.1, color: HEATMAP_COLORS[4], name: `${(step * 4).toFixed(1)}+ kW` },
  ];

  const options: ApexOptions = useMemo(() => ({
    chart: {
      type: "heatmap" as const,
      toolbar: { show: false },
      background: "transparent",
      // Cell-by-cell animation buys nothing on a heatmap and gets visibly
      // slow at the 90d view's ~2.2k cells.
      animations: { enabled: false },
    },
    theme: { mode: dark ? "dark" : "light" },
    dataLabels: { enabled: showDataLabels, style: { fontSize: "10px" } },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0,
        colorScale: { ranges },
        radius: 2,
      },
    },
    xaxis: {
      categories: Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}`),
      labels: { style: { fontSize: "0.6875rem" } },
      title: { text: "Hour of Day", style: { fontSize: "0.75rem" } },
    },
    yaxis: {
      labels: { style: { fontSize: "0.6875rem" } },
    },
    grid: { show: false },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }: any) => {
        const day = w.config.series[seriesIndex]?.name ?? "";
        const hour = w.config.series[seriesIndex]?.data[dataPointIndex]?.x ?? "";
        const val = w.config.series[seriesIndex]?.data[dataPointIndex]?.y ?? 0;
        return `<div style="padding:6px 10px;font-size:.7rem">${val} kW</div>`;
      },
    },
    legend: { show: false},
  }), [dark, showDataLabels, maxValue]);

  return (
    <ReactApexChart
      type="heatmap"
      series={series}
      options={options}
      height={height}
    />
  );
});
